import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { sub } from "date-fns";
import { ArchiveTimelineItemDc } from "@evergis/api/dist/__generated__/data-contracts";

import { api } from "../../../../../evergis/api";
import { useToggle } from "../../../../../hooks/useToggle";
import { getAppConfig } from "../../../../../ducks/appConfig";
import { DateFormat, formatDate } from "../../../../../utils/date";

const DEFAULT_ARCHIVED_PERIOD = "#24h";
const SNAPSHOTS_PER_PAGE = 10;

const getDuration = (archivePeriod: string): Record<string, number> => {
  const lastLetter = archivePeriod[archivePeriod.length - 1];
  const value = archivePeriod.slice(1, -1);

  const prop =
    lastLetter === "d"
      ? "days"
      : lastLetter === "h"
      ? "hours"
      : lastLetter === "m"
      ? "minutes"
      : lastLetter === "s"
      ? "seconds"
      : null;

  if (!lastLetter || !value || !prop) return {};

  return { [prop]: Number(value) };
};

export const useCameraAttribute = (cameraId: string) => {
  const appConfig = useSelector(getAppConfig);
  const archivePeriod = appConfig?.settings?.cameraAttribute?.archivePeriod || DEFAULT_ARCHIVED_PERIOD;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [timeline, setTimeline] = useState<ArchiveTimelineItemDc[]>([]);
  const [isLoadingSnapshot, setLoadingSnapshot] = useState(false);
  const [isLoadingTimeline, setLoadingTimeline] = useState(false);
  const [isOpenGallery, toggleGallery] = useToggle();
  const [galleryImages, setGalleryImages] = useState([]);
  const imagesRef = useRef([]);

  const onNextPage = useCallback(() => {
    setCurrentPage(currentPage + 1);
  }, [currentPage]);

  useEffect(() => {
    (async () => {
      if (!cameraId) return;

      setLoadingSnapshot(true);

      const snapshot = await api.cameras.getLiveSnapshot(cameraId);
      const url = URL.createObjectURL(snapshot);

      setLoadingSnapshot(false);
      setLoadingTimeline(true);

      imagesRef.current = [
        {
          src: url,
          startTime: new Date(),
        },
      ];

      setGalleryImages([
        {
          src: url,
          fileName: formatDate(new Date()),
        },
      ]);

      const formatDateOptions = { dateFormat: DateFormat.UTC };
      const startTime = formatDate(sub(new Date(), getDuration(archivePeriod)), formatDateOptions);
      const endTime = formatDate(new Date(), formatDateOptions);
      const timelineResponse = await api.cameras.getArchiveTimeline({ cameraId, startTime, endTime });

      if (!timelineResponse.success || !timelineResponse.timeline.length) return;

      timelineResponse.timeline.reverse();

      setTimeline(timelineResponse.timeline);
    })();
  }, [cameraId]);

  useEffect(() => {
    if (!timeline.length) return;

    const timelinePage = timeline.slice(currentPage * SNAPSHOTS_PER_PAGE, (currentPage + 1) * SNAPSHOTS_PER_PAGE);

    for (const item of timelinePage) {
      if (!item) continue;

      api.cameras
        .getArchiveSnapshot({
          cameraId,
          time: item.startTime,
        })
        .then(response => {
          const newImages = [
            ...imagesRef.current,
            { src: URL.createObjectURL(response), startTime: new Date(item.startTime) },
          ];

          newImages.sort((a, b) => (a.startTime < b.startTime ? 1 : a.startTime > b.startTime ? -1 : 0));
          imagesRef.current = newImages;

          setGalleryImages(
            newImages.map(newImage => ({
              src: newImage.src,
              fileName: formatDate(newImage.startTime, { dateFormat: DateFormat.DateTime }),
            })),
          );

          if (newImages.length === timelinePage.length) {
            setLoadingTimeline(false);
          }
        });
    }
  }, [timeline, currentPage]);

  return [
    {
      currentIndex,
      currentPage,
      totalCount: timeline.length,
      galleryImages,
      isLoadingSnapshot,
      isLoadingTimeline,
      isOpenGallery,
    },
    {
      toggleGallery,
      setGalleryImages,
      setCurrentIndex,
      onNextPage,
    },
  ];
};
